import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { IconCalendar, IconLocation } from "@components/ui/Icon"
import { useEventStatus } from "@hooks/useEventManifest"
import { formatDateTime } from "@utils/date-format"
import { getFilmBadges } from "@utils/film-badges"
import { Link, useHydrating } from "eddev/routing"
import { useMemo } from "react"
import { FilmSessionFragment, FilmTileFragment } from "../../types.graphql"
import { EventBadges } from "./EventBadges"

export type FilmTileProps = {
  film: FilmTileFragment
  session?: FilmSessionFragment
}

export function FilmTile(props: FilmTileProps) {
  const film = props.film
  const session = props.session

  const hydrating = useHydrating()
  const realtimeStatus = useEventStatus(session?.id ?? props.film.id)
  const badges = useMemo(
    () => getFilmBadges({ film, session, status: realtimeStatus?.status, hydrating }),
    [film, session, realtimeStatus?.status, hydrating],
  )

  const content = (
    <Link href={film.uri} className="flex flex-col gap-2 relative antialiased group">
      {/* Image */}
      <div className="aspect-video bg-secondary overflow-hidden">
        <ResponsiveImage
          {...film.media?.thumb!}
          aspect={16 / 9}
          className="origin-center transition-transform md:group-hover:scale-110 duration-500 w-full h-full"
        />
      </div>

      <div className="absolute top-0 left-2 flex gap-2 -translate-y-1/2">
        <EventBadges badges={badges} />
      </div>

      {/* Info */}
      <div className="flex flex-col gap-2 pt-2 pb-3">
        {/* Film title */}
        <div className="type-title-xs">{film.title}</div>

        {/* Excerpt */}
        {film.excerpt && (
          <div className="type-body-xs line-clamp-3" dangerouslySetInnerHTML={{ __html: film.excerpt }} />
        )}

        {/* Session info */}
        {session && (
          <div className="flex flex-col gap-1">
            {/* Date */}
            {session?.info?.dateTime && (
              <Button
                aria-label="Showing on"
                size="sm"
                variant="secondary"
                className="w-fit px-2"
                leftIcon={<IconCalendar />}
              >
                {formatDateTime(session.info?.dateTime)}
              </Button>
            )}
            {/* Venue */}
            {session?.info?.venue && (
              <Button
                aria-label="Showing at"
                size="sm"
                variant="secondary"
                className="w-fit px-2"
                leftIcon={<IconLocation />}
              >
                <span>{session?.info?.venue?.titleSimple}</span>
              </Button>
            )}
          </div>
        )}
      </div>
    </Link>
  )

  return content
}
